/**
 * Mode resolver — reads mode config, applies mode-level overrides to fields.
 *
 * `applyOverridesToField` resolves static boolean overrides at merge time.
 * `getConditionalOverrides` collects Condition-based overrides for runtime
 * evaluation in fieldStateEvaluator (FormFieldAdapter path).
 */

import type { FormSchema, FormMode, ModeOverrides, ViewPresentation } from '../types/schema';
import type { FieldOverride, BlockOverrides } from '../types/block';
import type { Condition } from '../types/conditions';

export interface ResolvedMode {
  mode: FormMode;
  isReadOnly: boolean;
  viewPresentation: ViewPresentation;
}

export function resolveMode(schema: FormSchema, modeName: string): ResolvedMode {
  const mode = schema.modes[modeName];
  if (!mode) {
    throw new Error(`[form-engine] Mode "${modeName}" not found in schema "${schema.metadata.semanticId}"`);
  }

  const isViewMode = modeName === 'view';
  const isReadOnly =
    isViewMode || !mode.dataSource?.submit || mode.overrides?.readOnly === true;

  // Form modes (create/edit): read-only fields keep input chrome — disabled controls in the matrix.
  // View mode: read-only fields render as label + formatted text unless explicitly overridden.
  const defaultViewPresentation: ViewPresentation = isViewMode ? 'label-value' : 'disabled-inputs';
  const viewPresentation: ViewPresentation = mode.viewPresentation ?? defaultViewPresentation;

  return { mode, isReadOnly, viewPresentation };
}

export interface FieldState {
  readOnly: boolean;
  hidden: boolean;
  disabled: boolean;
  required: boolean;
}

export function applyOverridesToField(
  fieldName: string,
  baseRequired: boolean,
  modeOverrides?: ModeOverrides,
  blockOverrides?: BlockOverrides,
): FieldState {
  const state: FieldState = {
    readOnly: false,
    hidden: false,
    disabled: false,
    required: baseRequired,
  };

  if (modeOverrides?.readOnly === true) {
    state.readOnly = true;
  }
  if (modeOverrides?.disabled === true) {
    state.disabled = true;
  }

  const modeFieldOverride = modeOverrides?.fields?.[fieldName];
  if (modeFieldOverride) {
    applyFieldOverride(state, modeFieldOverride);
  }

  if (blockOverrides?.readOnly === true) {
    state.readOnly = true;
  }
  if (blockOverrides?.hidden === true) {
    state.hidden = true;
  }
  if (blockOverrides?.disabled === true) {
    state.disabled = true;
  }

  const blockFieldOverride = blockOverrides?.fields?.[fieldName];
  if (blockFieldOverride) {
    applyFieldOverride(state, blockFieldOverride);
  }

  return state;
}

function applyFieldOverride(state: FieldState, override: FieldOverride): void {
  if (override.readOnly === true) state.readOnly = true;
  if (override.hidden === true) state.hidden = true;
  if (override.disabled === true) state.disabled = true;
  if (override.required === true) state.required = true;
  if (override.required === false) state.required = false;
}

export function getConditionalOverrides(
  fieldName: string,
  modeOverrides?: ModeOverrides,
  blockOverrides?: BlockOverrides,
): Record<string, Condition> {
  const conditions: Record<string, Condition> = {};

  if (modeOverrides?.readOnly && typeof modeOverrides.readOnly === 'object') {
    conditions.readOnly = modeOverrides.readOnly;
  }
  if (modeOverrides?.disabled && typeof modeOverrides.disabled === 'object') {
    conditions.disabled = modeOverrides.disabled;
  }

  const modeField = modeOverrides?.fields?.[fieldName];
  if (modeField) {
    collectConditionalOverride(conditions, modeField);
  }

  if (blockOverrides?.readOnly && typeof blockOverrides.readOnly === 'object') {
    conditions.readOnly = blockOverrides.readOnly;
  }
  if (blockOverrides?.hidden && typeof blockOverrides.hidden === 'object') {
    conditions.hidden = blockOverrides.hidden;
  }
  if (blockOverrides?.disabled && typeof blockOverrides.disabled === 'object') {
    conditions.disabled = blockOverrides.disabled;
  }

  const blockField = blockOverrides?.fields?.[fieldName];
  if (blockField) {
    collectConditionalOverride(conditions, blockField);
  }

  return conditions;
}

function collectConditionalOverride(
  target: Record<string, Condition>,
  override: FieldOverride,
): void {
  if (override.readOnly && typeof override.readOnly === 'object') {
    target.readOnly = override.readOnly;
  }
  if (override.hidden && typeof override.hidden === 'object') {
    target.hidden = override.hidden;
  }
  if (override.disabled && typeof override.disabled === 'object') {
    target.disabled = override.disabled;
  }
  if (override.required && typeof override.required === 'object') {
    target.required = override.required;
  }
}
